import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { proxiedImage, onImgError } from '../utils/images';
import { getOwnerKey, useAuth } from '../hooks/useAuth';

interface ListItem {
  id: number;
  name: string;
  price: number;
  list_price?: number;
  image: string;
  asin?: string;
  handle?: string;
  rating: number;
  healthScore: number;
  category: string;
  category_name: string;
  note?: string;
}

interface ListDetail {
  token: string;
  title: string;
  description: string;
  cover_image: string;
  saves: number;
  created_at: string;
  items: ListItem[];
}

const ListPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [list, setList] = useState<ListDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const { authHeaders } = useAuth();

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`/api/lists/${token}`)
      .then(async (r) => {
        if (!active) return;
        if (r.ok) setList(await r.json());
        setLoading(false);
      })
      .catch(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const saveList = async () => {
    await fetch(`/api/lists/${token}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ saverKey: getOwnerKey() }),
    });
    setSaved(true);
  };

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link to share the list:', url);
    }
  };

  if (loading) return <div className="container-custom py-28 flex justify-center"><LoadingSpinner /></div>;
  if (!list) return (
    <div className="container-custom py-24 text-center">
      <h1 className="mb-4">List not found</h1>
      <p className="text-earth-600 mb-8">This list may have been removed.</p>
      <Link to="/lists" className="btn btn-primary">Browse lists</Link>
    </div>
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: list.title,
    description: list.description,
    url: `https://goodcart.org/lists/${list.token}`,
    numberOfItems: list.items.length,
    itemListElement: list.items.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://goodcart.org/products/${p.handle || p.id}`,
      name: p.name,
    })),
  };

  return (
    <div className="min-h-screen bg-cream-100">
      <SEO
        title={list.title}
        description={list.description || `${list.title}: a curated GoodCart list with ${list.items.length} clean picks.`}
        image={list.cover_image ? proxiedImage(list.cover_image) : '/og-image.png'}
        url={`https://goodcart.org/lists/${list.token}`}
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <section className="relative bg-sage-700 text-cream-50 overflow-hidden">
        {list.cover_image && (
          <img src={proxiedImage(list.cover_image)} alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25" onError={onImgError()} />
        )}
        <div className="container-custom py-8 md:py-10 relative">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <nav className="text-xs text-sage-200 mb-2">
              <Link to="/lists" className="hover:text-cream-50">All lists</Link>
              <span aria-hidden> / </span>
              <span className="text-cream-50">{list.title}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-display">{list.title}</h1>
            {list.description && <p className="mt-2 max-w-2xl text-sm text-sage-100/90">{list.description}</p>}
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={saveList}
                disabled={saved}
                className="btn bg-cream-50 text-sage-800 hover:bg-white btn-sm"
              >
                {saved ? 'Saved to your lists' : `Save list (${list.saves})`}
              </button>
              <button type="button" onClick={share} className="btn btn-outline btn-sm !text-cream-50 !border-cream-200/60 hover:!bg-sage-600">
                {copied ? 'Link copied' : 'Share'}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-8">
        {list.items.length === 0 ? (
          <p className="text-earth-600 text-center py-12">This list is empty.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {list.items.map((p, i) => (
              <ProductCard key={p.id} product={p as any} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListPage;
